import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Confirm, Report } from 'notiflix'
import api from '../../../helpers/apiRequester'
import { Button, Card, Checkbox, EmptyState, Field, FieldGrid, Input, NativeSelect, PillTone, StatusPill } from '../../../ui'
import styles from '../commsShared/admin.module.scss'
import { ExportsSection } from './ExportsSection'

interface Category {
    key: string
    label: string
    description: string | null
    partyScope: string
    suggestedNextAction: string | null
    nextActionLabel: string | null
    skipExtraction: boolean
    affectsOutcome: boolean
    sortOrder: number
    active: boolean
    promotedFromProposal: boolean
    counts: { total: number; byLlm: number; byRules: number; byHuman: number; byLegacy: number } | null
}

interface Proposal {
    proposedCategory: string
    occurrences: number
    lastSeen: string
    sampleRationales: string[]
    sampleInboundIds: string[]
}

interface Rule {
    id: string
    name: string
    priority: number
    matcherKind: string
    matcherConfig: Record<string, unknown>
    classification: string
    setTriageState: string | null
    confidence: number
    mode: 'active' | 'shadow' | 'disabled'
    notes: string | null
    stats: { hits: number; agree: number; disagree: number } | null
}

interface FactType {
    key: string
    label: string
    group: string
    dataType: string
    compareWith: string | null
    isIdentifier: boolean
}

const humanise = (v: string | null | undefined) => (v ? v.replace(/_/g, ' ') : '—')
const MODE_TONE: Record<Rule['mode'], PillTone> = { active: 'good', shadow: 'warn', disabled: 'neutral' }
const MATCHER_KINDS = ['header', 'from_domain', 'from_address', 'subject_regex', 'body_regex']

const emptyCategory = { key: '', label: '', description: '', partyScope: 'lender', suggestedNextAction: '', nextActionLabel: '', skipExtraction: false, affectsOutcome: true, sortOrder: 100 }
const emptyRule = { name: '', priority: 100, matcherKind: 'body_regex', pattern: '', domain: '', headerName: '', classification: '', confidence: 70, mode: 'shadow', setTriageState: '', notes: '' }

export const CommsCategoriesView = () => {
    const navigate = useNavigate()
    const [categories, setCategories] = useState<Category[]>([])

    /** Drill into the cross-mailbox conversation list filtered to this category. */
    const openInInbox = (key: string, _source?: 'llm' | 'rules' | 'human' | 'legacy') => {
        const params = new URLSearchParams({ tab: 'received', category: key })
        navigate(`/comms/mailboxes/all?${params.toString()}`)
    }

    const CountLink = ({ value, onClick, title }: { value: number | undefined; onClick: () => void; title: string }) =>
        value ? (
            <Button size="sm" variant="link" onClick={onClick} title={title}>
                {value.toLocaleString()}
            </Button>
        ) : (
            <span className={styles.sub}>0</span>
        )
    const [factTypes, setFactTypes] = useState<FactType[]>([])
    const [proposals, setProposals] = useState<Proposal[]>([])
    const [rules, setRules] = useState<Rule[]>([])
    const [busy, setBusy] = useState<string | null>(null)
    const [catDraft, setCatDraft] = useState<typeof emptyCategory | null>(null)
    const [ruleDraft, setRuleDraft] = useState<typeof emptyRule | null>(null)
    const [showInactive, setShowInactive] = useState(false)

    const load = useCallback(async () => {
        try {
            const [c, p, r] = await Promise.all([
                api({ url: 'comms/categories/list', data: { includeInactive: true } }),
                api({ url: 'comms/categories/proposals', data: {} }),
                api({ url: 'comms/categories/rules', data: {} }),
            ])
            if (c.data?.success) {
                setCategories(c.data.categories || [])
                setFactTypes(c.data.factTypes || [])
            }
            if (p.data?.success) setProposals(p.data.proposals || [])
            if (r.data?.success) setRules(r.data.rules || [])
        } catch (err: any) {
            Report.failure('Load failed', err?.response?.data?.message || 'Could not load categories.', 'Okay')
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const post = async (label: string, url: string, data: Record<string, unknown>) => {
        setBusy(label)
        try {
            const resp = await api({ url, data })
            if (resp.data?.success === false) Report.warning(label, resp.data.message || 'Did not complete.', 'Okay')
            await load()
            return resp.data
        } catch (err: any) {
            Report.failure(`${label} failed`, err?.response?.data?.message || 'Please try again.', 'Okay')
        } finally {
            setBusy(null)
        }
    }

    const handlePromote = (p: Proposal) => {
        const label = window.prompt(`Label for the new category "${p.proposedCategory}":`, p.proposedCategory.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
        if (label === null) return
        Confirm.show(
            'Promote category',
            `Create "${p.proposedCategory}" and re-label the ${p.occurrences} email(s) the model put in "other" with this proposal?`,
            'Promote',
            'Cancel',
            async () => {
                const data = await post('Promote', 'comms/categories/promote', { proposedCategory: p.proposedCategory, label, description: p.sampleRationales[0] || null })
                if (data?.success) Report.success('Promoted', `${data.relabelled} email(s) re-labelled as "${p.proposedCategory}".`, 'Okay')
            }
        )
    }

    const saveCategory = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!catDraft) return
        await post('Save category', 'comms/categories/upsert', {
            ...catDraft,
            suggestedNextAction: catDraft.suggestedNextAction || null,
            nextActionLabel: catDraft.nextActionLabel || null,
            description: catDraft.description || null,
        })
        setCatDraft(null)
    }

    const saveRule = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!ruleDraft) return
        const matcherConfig: Record<string, unknown> =
            ruleDraft.matcherKind === 'from_domain'
                ? { domains: ruleDraft.domain.split(',').map((d) => d.trim()).filter(Boolean) }
                : ruleDraft.matcherKind === 'header'
                  ? { name: ruleDraft.headerName, pattern: ruleDraft.pattern || '.' }
                  : { pattern: ruleDraft.pattern }
        await post('Save rule', 'comms/categories/rule-upsert', {
            name: ruleDraft.name,
            priority: Number(ruleDraft.priority),
            matcherKind: ruleDraft.matcherKind,
            matcherConfig,
            classification: ruleDraft.classification,
            confidence: Number(ruleDraft.confidence),
            mode: ruleDraft.mode,
            setTriageState: ruleDraft.setTriageState || null,
            notes: ruleDraft.notes || null,
        })
        setRuleDraft(null)
    }

    const visible = categories.filter((c) => showInactive || c.active)
    const activeCategoryOptions = categories.filter((c) => c.active).map((c) => ({ value: c.key, label: c.label }))

    return (
        <div className={styles.stack}>
            <Card
                as="section"
                title="Categories"
                subtitle="The LLM prompt is built from this list, so a new category is live for the next email. Counts show who classified each email — click one to open those emails in the inbox."
                actions={
                    <div className={styles.actions}>
                        <Checkbox label="Show inactive" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
                        <Button size="sm" onClick={() => setCatDraft(emptyCategory)}>
                            New category
                        </Button>
                    </div>
                }
            >
                {catDraft && (
                    <form className={styles.inlineForm} onSubmit={saveCategory}>
                        <FieldGrid>
                            <Field label="Key" hint="snake_case" required>
                                {(id) => <Input id={id} mono required pattern="[a-z][a-z0-9_]{1,62}" value={catDraft.key} onChange={(e) => setCatDraft({ ...catDraft, key: e.target.value })} />}
                            </Field>
                            <Field label="Label" required>
                                {(id) => <Input id={id} required value={catDraft.label} onChange={(e) => setCatDraft({ ...catDraft, label: e.target.value })} />}
                            </Field>
                            <Field label="Description" hint="Shown to the model" span={2}>
                                {(id) => <Input id={id} value={catDraft.description} onChange={(e) => setCatDraft({ ...catDraft, description: e.target.value })} />}
                            </Field>
                            <Field label="Next action key">
                                {(id) => <Input id={id} mono value={catDraft.suggestedNextAction} onChange={(e) => setCatDraft({ ...catDraft, suggestedNextAction: e.target.value })} placeholder="e.g. send_info_response" />}
                            </Field>
                            <Field label="Next action label">
                                {(id) => <Input id={id} value={catDraft.nextActionLabel} onChange={(e) => setCatDraft({ ...catDraft, nextActionLabel: e.target.value })} />}
                            </Field>
                            <Field label="Party">
                                {(id) => (
                                    <NativeSelect
                                        id={id}
                                        value={catDraft.partyScope}
                                        onChange={(e) => setCatDraft({ ...catDraft, partyScope: e.target.value })}
                                        options={[
                                            { value: 'lender', label: 'lender' },
                                            { value: 'client', label: 'client' },
                                            { value: 'any', label: 'any' },
                                        ]}
                                    />
                                )}
                            </Field>
                            <Field span="all">
                                <Checkbox label="Skip extraction" hint="Noise-like categories that do not need data pulled out" checked={catDraft.skipExtraction} onChange={(e) => setCatDraft({ ...catDraft, skipExtraction: e.target.checked })} />
                            </Field>
                            <Field span="all">
                                <Checkbox
                                    label="Moves the thread outcome"
                                    hint="Untick for automated or off-topic mail (bounces, auto replies, sales). An email in such a category still counts as received but never changes where its thread stands."
                                    checked={catDraft.affectsOutcome}
                                    onChange={(e) => setCatDraft({ ...catDraft, affectsOutcome: e.target.checked })}
                                />
                            </Field>
                        </FieldGrid>
                        <div className={styles.formFooter}>
                            <Button type="submit" size="sm" variant="primary" disabled={busy !== null}>
                                Save
                            </Button>
                            <Button type="button" size="sm" onClick={() => setCatDraft(null)}>
                                Cancel
                            </Button>
                        </div>
                    </form>
                )}
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Key</th>
                                <th>Label</th>
                                <th>Description</th>
                                <th>Next action</th>
                                <th>Total</th>
                                <th>LLM</th>
                                <th>Rules</th>
                                <th>Human</th>
                                <th>Legacy</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((c) => (
                                <tr key={c.key} className={c.active ? undefined : styles.rowInactive}>
                                    <td>
                                        <div className={styles.actions}>
                                            <span className={styles.mono}>{c.key}</span>
                                            {c.skipExtraction && <StatusPill size="sm">no extraction</StatusPill>}
                                            {!c.affectsOutcome && (
                                                <StatusPill size="sm" title="Emails in this category never move a thread's outcome">
                                                    no outcome
                                                </StatusPill>
                                            )}
                                            {c.promotedFromProposal && (
                                                <StatusPill size="sm" tone="info">
                                                    promoted
                                                </StatusPill>
                                            )}
                                        </div>
                                    </td>
                                    <td>{c.label}</td>
                                    <td className={styles.sub}>{c.description}</td>
                                    <td className={styles.sub}>{c.nextActionLabel || humanise(c.suggestedNextAction)}</td>
                                    <td>
                                        <CountLink value={c.counts?.total} onClick={() => openInInbox(c.key)} title={`Open every "${c.label}" email in the inbox`} />
                                    </td>
                                    <td>
                                        <CountLink value={c.counts?.byLlm} onClick={() => openInInbox(c.key, 'llm')} title="Classified by the model" />
                                    </td>
                                    <td>
                                        <CountLink value={c.counts?.byRules} onClick={() => openInInbox(c.key, 'rules')} title="Classified by a rule" />
                                    </td>
                                    <td>
                                        <CountLink value={c.counts?.byHuman} onClick={() => openInInbox(c.key, 'human')} title="Classified by a person" />
                                    </td>
                                    <td>
                                        <CountLink value={c.counts?.byLegacy} onClick={() => openInInbox(c.key, 'legacy')} title="Imported from the legacy pipeline" />
                                    </td>
                                    <td>
                                        <div className={styles.rowActions}>
                                            <Button size="sm" variant="link" onClick={() => openInInbox(c.key)} disabled={!c.counts?.total}>
                                                View emails
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="link"
                                                onClick={() =>
                                                    setCatDraft({
                                                        key: c.key,
                                                        label: c.label,
                                                        description: c.description || '',
                                                        partyScope: c.partyScope,
                                                        suggestedNextAction: c.suggestedNextAction || '',
                                                        nextActionLabel: c.nextActionLabel || '',
                                                        skipExtraction: c.skipExtraction,
                                                        affectsOutcome: c.affectsOutcome,
                                                        sortOrder: c.sortOrder,
                                                    })
                                                }
                                            >
                                                Edit
                                            </Button>
                                            <Button size="sm" variant="link" disabled={busy !== null} onClick={() => post('Toggle', 'comms/categories/set-active', { key: c.key, active: !c.active })}>
                                                {c.active ? 'Deactivate' : 'Activate'}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <ExportsSection />

            <div className={styles.twoCol}>
                <Card
                    as="section"
                    title={
                        <>
                            Proposed by the model
                            <StatusPill tone={proposals.length ? 'warn' : 'neutral'} round size="sm">
                                {proposals.length}
                            </StatusPill>
                        </>
                    }
                    subtitle='When nothing fits, the model files an email under "other" and proposes a key. Promote the ones that recur.'
                >
                    {proposals.length === 0 ? (
                        <EmptyState bare title="No proposals outstanding" />
                    ) : (
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Proposed key</th>
                                        <th>Emails</th>
                                        <th>Why</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {proposals.map((p) => (
                                        <tr key={p.proposedCategory}>
                                            <td className={styles.mono}>{p.proposedCategory}</td>
                                            <td>{p.occurrences}</td>
                                            <td className={styles.sub}>{p.sampleRationales[0]}</td>
                                            <td>
                                                <Button size="sm" disabled={busy !== null} onClick={() => handlePromote(p)}>
                                                    Promote
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>

                <Card as="section" title="Extracted data points" subtitle="What the model is asked to pull out of every email, and which field on our record each one is checked against.">
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Group</th>
                                    <th>Data point</th>
                                    <th>Type</th>
                                    <th>Compared with</th>
                                </tr>
                            </thead>
                            <tbody>
                                {factTypes.map((t) => (
                                    <tr key={t.key}>
                                        <td className={styles.sub}>{t.group}</td>
                                        <td>
                                            <div className={styles.actions}>
                                                {t.label}
                                                {t.isIdentifier && (
                                                    <StatusPill size="sm" tone="info">
                                                        identifier
                                                    </StatusPill>
                                                )}
                                            </div>
                                        </td>
                                        <td className={styles.sub}>{t.dataType}</td>
                                        <td className={styles.mono}>{t.compareWith || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>

            <Card
                as="section"
                title="Rules"
                subtitle="Active rules classify without a model call (and can archive noise). Shadow rules only record hits so you can see how often they agree with the model before switching them on."
                actions={
                    <Button size="sm" onClick={() => setRuleDraft(emptyRule)}>
                        New rule
                    </Button>
                }
            >
                {ruleDraft && (
                    <form className={styles.inlineForm} onSubmit={saveRule}>
                        <FieldGrid>
                            <Field label="Name" required>
                                {(id) => <Input id={id} required value={ruleDraft.name} onChange={(e) => setRuleDraft({ ...ruleDraft, name: e.target.value })} />}
                            </Field>
                            <Field label="Matcher">
                                {(id) => <NativeSelect id={id} value={ruleDraft.matcherKind} onChange={(e) => setRuleDraft({ ...ruleDraft, matcherKind: e.target.value })} options={MATCHER_KINDS.map((k) => ({ value: k, label: humanise(k) }))} />}
                            </Field>
                            {ruleDraft.matcherKind === 'from_domain' ? (
                                <Field label="Domains" hint="Comma separated" required span={2}>
                                    {(id) => <Input id={id} required value={ruleDraft.domain} onChange={(e) => setRuleDraft({ ...ruleDraft, domain: e.target.value })} />}
                                </Field>
                            ) : (
                                <>
                                    {ruleDraft.matcherKind === 'header' && (
                                        <Field label="Header name" required>
                                            {(id) => <Input id={id} mono required value={ruleDraft.headerName} onChange={(e) => setRuleDraft({ ...ruleDraft, headerName: e.target.value })} />}
                                        </Field>
                                    )}
                                    <Field label="Pattern" hint="Regex, case-insensitive" required={ruleDraft.matcherKind !== 'header'} span={ruleDraft.matcherKind === 'header' ? 1 : 2}>
                                        {(id) => <Input id={id} mono required={ruleDraft.matcherKind !== 'header'} value={ruleDraft.pattern} onChange={(e) => setRuleDraft({ ...ruleDraft, pattern: e.target.value })} />}
                                    </Field>
                                </>
                            )}
                            <Field label="Category" required>
                                {(id) => <NativeSelect id={id} required placeholder="Choose…" value={ruleDraft.classification} onChange={(e) => setRuleDraft({ ...ruleDraft, classification: e.target.value })} options={activeCategoryOptions} />}
                            </Field>
                            <Field label="Priority" hint="Lower runs first">
                                {(id) => <Input id={id} type="number" value={ruleDraft.priority} onChange={(e) => setRuleDraft({ ...ruleDraft, priority: Number(e.target.value) })} />}
                            </Field>
                            <Field label="Confidence">
                                {(id) => <Input id={id} type="number" min={0} max={100} value={ruleDraft.confidence} onChange={(e) => setRuleDraft({ ...ruleDraft, confidence: Number(e.target.value) })} />}
                            </Field>
                            <Field label="Mode">
                                {(id) => (
                                    <NativeSelect
                                        id={id}
                                        value={ruleDraft.mode}
                                        onChange={(e) => setRuleDraft({ ...ruleDraft, mode: e.target.value })}
                                        options={[
                                            { value: 'shadow', label: 'shadow' },
                                            { value: 'active', label: 'active' },
                                            { value: 'disabled', label: 'disabled' },
                                        ]}
                                    />
                                )}
                            </Field>
                            <Field label="Also set triage">
                                {(id) => (
                                    <NativeSelect
                                        id={id}
                                        value={ruleDraft.setTriageState}
                                        onChange={(e) => setRuleDraft({ ...ruleDraft, setTriageState: e.target.value })}
                                        options={[
                                            { value: '', label: 'no change' },
                                            { value: 'archived', label: 'archived' },
                                            { value: 'resolved', label: 'resolved' },
                                            { value: 'snoozed', label: 'snoozed' },
                                        ]}
                                    />
                                )}
                            </Field>
                        </FieldGrid>
                        <div className={styles.formFooter}>
                            <Button type="submit" size="sm" variant="primary" disabled={busy !== null}>
                                Save
                            </Button>
                            <Button type="button" size="sm" onClick={() => setRuleDraft(null)}>
                                Cancel
                            </Button>
                        </div>
                    </form>
                )}
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Priority</th>
                                <th>Name</th>
                                <th>Matcher</th>
                                <th>Category</th>
                                <th>Mode</th>
                                <th>Hits (30d)</th>
                                <th>Agrees with model</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {rules.map((r) => {
                                const agreePct = r.stats && r.stats.agree + r.stats.disagree > 0 ? Math.round((r.stats.agree / (r.stats.agree + r.stats.disagree)) * 100) : null
                                return (
                                    <tr key={r.id}>
                                        <td>{r.priority}</td>
                                        <td>
                                            <div className={styles.actions}>
                                                {r.name}
                                                {r.setTriageState && <StatusPill size="sm">→ {r.setTriageState}</StatusPill>}
                                            </div>
                                        </td>
                                        <td className={styles.mono} title={JSON.stringify(r.matcherConfig)}>
                                            {r.matcherKind}: {String(r.matcherConfig.pattern ?? (r.matcherConfig.domains as string[] | undefined)?.join(', ') ?? r.matcherConfig.name ?? '')}
                                        </td>
                                        <td>{humanise(r.classification)}</td>
                                        <td>
                                            <StatusPill size="sm" tone={MODE_TONE[r.mode]} dot>
                                                {r.mode}
                                            </StatusPill>
                                        </td>
                                        <td>{r.stats?.hits ?? 0}</td>
                                        <td>
                                            {agreePct == null ? (
                                                '—'
                                            ) : (
                                                <StatusPill size="sm" tone={agreePct >= 90 ? 'good' : agreePct >= 70 ? 'warn' : 'bad'}>
                                                    {agreePct}%
                                                </StatusPill>
                                            )}
                                        </td>
                                        <td>
                                            <div className={styles.rowActions}>
                                                {(['active', 'shadow', 'disabled'] as const)
                                                    .filter((m) => m !== r.mode)
                                                    .map((m) => (
                                                        <Button key={m} size="sm" variant="link" disabled={busy !== null} onClick={() => post('Rule mode', 'comms/categories/rule-set-mode', { id: r.id, mode: m })}>
                                                            {m === 'active' ? 'Activate' : m === 'shadow' ? 'Shadow' : 'Disable'}
                                                        </Button>
                                                    ))}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    )
}
